import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, TextField, Switch, Alert,
  useMediaQuery, useTheme, Tab, Tabs,
} from '@mui/material';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, FitnessCenter as WellnessIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import { useAuth } from '../context/AuthContext';
import { wellnessConfigService, WellnessConfig, WellnessConfigCreate, WellnessConfigUpdate } from '../services/wellnessConfigService';
import { wellnessItemService, WellnessItem } from '../services/wellnessItemService';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { wellnessConfigColumns } from '../config/wellnessConfigColumns';
import { formatDate, formatDateTime, convertUTCToTimezoneInput, convertTimezoneInputToUTC } from '../utils/dateUtils';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { useCrudHandlers } from '../hooks/useCrudHandlers';
import WellnessCategoriesTab from '../components/wellness/WellnessCategoriesTab';
import WellnessCategoryDialog from '../components/wellness/WellnessCategoryDialog';
import { WellnessCategory } from '../types/wellnessCategory';

type FormData = Omit<WellnessConfigCreate & WellnessConfigUpdate, 'data'>;

const WellnessConfigPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const userTimezone = useUserTimezone();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Config dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WellnessConfig | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<WellnessConfig | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'categories'>('overview');

  // Categories state
  const [categories, setCategories] = useState<WellnessCategory[]>([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<WellnessCategory | null>(null);
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<WellnessCategory | null>(null);
  const [wellnessItems, setWellnessItems] = useState<WellnessItem[]>([]);

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<FormData>();

  const getSortValue = useCallback((item: WellnessConfig, key: string): any => {
    switch (key) {
      case 'name': return item.name.toLowerCase();
      case 'priority': return item.priority;
      case 'active': return item.active ? 1 : 0;
      case 'fromDate': return item.from_date || '';
      case 'toDate': return item.to_date || '';
      case 'fromMobileVersion': return item.from_mobile_version || '';
      case 'createdAt': return item.created_at;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((item: WellnessConfig): string[] => [item.name], []);

  const table = useTablePage<WellnessConfig>({
    entityType: 'wellness-configs',
    columns: wellnessConfigColumns,
    tenantId: currentUser?.tenant_id,
    getSortValue,
    getSearchText,
  });

  const crud = useCrudHandlers<WellnessConfig>({
    table,
    createFn: (data) => wellnessConfigService.create(currentUser!.tenant_id!, data),
    updateFn: (id, data) => wellnessConfigService.update(id as number, data),
    deleteFn: (id) => wellnessConfigService.delete(id as number),
    entityName: 'Wellness Config',
    onSuccess: () => { setAddDialogOpen(false); setEditingItem(null); reset(); setActiveTab('overview'); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); setConfigToDelete(null); },
  });

  useEffect(() => {
    if (currentUser?.user_privilege?.wellness) {
      table.loadData(() => wellnessConfigService.getAll(currentUser.tenant_id ?? undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (addDialogOpen && currentUser?.tenant_id) {
      wellnessItemService.getAll(currentUser.tenant_id).then((items) => {
        setWellnessItems(items.filter((i) => i.type === 'category'));
      }).catch(() => {});
    }
  }, [addDialogOpen, currentUser]);

  const openAdd = () => {
    setEditingItem(null);
    setCategories([]);
    setActiveTab('overview');
    reset({ name: '', priority: 0, active: true, from_date: null, to_date: null, from_mobile_version: '' });
    setAddDialogOpen(true);
  };

  const openEdit = (item: WellnessConfig) => {
    setEditingItem(item);
    setCategories((item.data?.categories ?? []).map((c: WellnessCategory) => ({ ...c, _key: c._key ?? Math.random().toString(36).slice(2) })));
    setActiveTab('overview');
    reset({
      name: item.name,
      priority: item.priority,
      active: item.active,
      from_date: item.from_date ? convertUTCToTimezoneInput(item.from_date, userTimezone) : null,
      to_date: item.to_date ? convertUTCToTimezoneInput(item.to_date, userTimezone) : null,
      from_mobile_version: item.from_mobile_version || '',
    });
    setAddDialogOpen(true);
  };

  const handleEdit = () => { if (table.selectedItem) openEdit(table.selectedItem); table.handleMenuClose(); };
  const handleDelete = () => {
    if (table.selectedItem) { setConfigToDelete(table.selectedItem); setDeleteDialogOpen(true); }
    table.handleMenuClose();
  };
  const onSubmit = (rest: FormData) => {
    const payload = {
      ...rest,
      from_date: rest.from_date ? convertTimezoneInputToUTC(rest.from_date as string, userTimezone) : null,
      to_date: rest.to_date ? convertTimezoneInputToUTC(rest.to_date as string, userTimezone) : null,
      data: { categories },
    };
    crud.handleCreateOrUpdate(payload, editingItem);
  };

  const getContextMenuOptions = (): ContextMenuOption[] => [
    { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: handleEdit },
    { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: handleDelete, color: 'error' },
  ];

  const getColumnValue = (item: WellnessConfig, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return (
          <Typography variant="body2" onClick={(e) => { e.stopPropagation(); openEdit(item); }}
            sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main', '&:hover': { color: 'primary.dark' } }}>
            {item.name}
          </Typography>
        );
      case 'priority': return item.priority;
      case 'active': return <Chip label={item.active ? 'Yes' : 'No'} color={item.active ? 'success' : 'default'} size="small" />;
      case 'fromDate': return item.from_date ? formatDateTime(item.from_date, {}, userTimezone) : '—';
      case 'toDate': return item.to_date ? formatDateTime(item.to_date, {}, userTimezone) : '—';
      case 'fromMobileVersion': return item.from_mobile_version || '—';
      case 'createdAt': return formatDate(item.created_at, userTimezone);
      default: return 'N/A';
    }
  };

  const renderMobileCard = (item: WellnessConfig) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} sx={{ pr: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>{item.name}</Typography>
        <Chip label={item.active ? 'Active' : 'Inactive'} color={item.active ? 'success' : 'default'} size="small" />
      </Box>
      <Typography variant="body2" color="text.secondary">Priority: {item.priority}</Typography>
    </>
  );

  // Category handlers
  const handleSaveCategory = (category: WellnessCategory) => {
    setCategories((prev) =>
      editingCategory
        ? prev.map((c) => c._key === editingCategory._key ? category : c)
        : [...prev, category]
    );
    setCategoryDialogOpen(false);
  };

  const handleConfirmDeleteCategory = () => {
    if (deletingCategory) {
      setCategories((prev) => prev.filter((c) => c._key !== deletingCategory._key));
    }
    setDeleteCategoryDialogOpen(false);
    setDeletingCategory(null);
  };

  if (!currentUser?.user_privilege?.wellness) {
    return <Alert severity="error">You don't have permission to access this page.</Alert>;
  }

  return (
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading wellness configs:">
      <DataTableToolbar
        {...table.toolbarProps}
        title="Wellness Config"
        searchPlaceholder="Search wellness configs..."
        isAdmin={!!currentUser?.user_privilege?.config}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ whiteSpace: 'nowrap' }}>
            Add Config
          </Button>
        }
      />
      {isMobile ? (
        <MobileCardGrid data={table.sortedData} renderCard={renderMobileCard} onMenuClick={table.handleRowClick}
          getItemKey={(i) => i.id} emptyIcon={<WellnessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No wellness configs found" />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody data={table.sortedData} {...table.tableBodyProps} getColumnValue={getColumnValue}
            onRowClick={table.handleRowClick} getRowKey={(i) => i.id} />
        </DataTableContainer>
      )}

      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      {/* Main config dialog */}
      <ClinicDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        title={editingItem ? 'Edit Wellness Config' : 'Add Wellness Config'}
        closeButtonSide="left"
        onSubmit={handleSubmit(onSubmit)}
        actions={
          <>
            <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
              {editingItem ? 'Update' : 'Create'}
            </Button>
            <Button onClick={() => setAddDialogOpen(false)} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
          </>
        }
      >
        <Box sx={{ minWidth: 480 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Overview" value="overview" />

            <Tab label="Categories" value="categories" disabled={!editingItem} />

          </Tabs>

          {activeTab === 'overview' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
                <TextField {...register('name', { required: 'Name is required' })} fullWidth size="small"
                  error={!!errors.name} helperText={errors.name?.message as string}
                  inputProps={{ dir: 'rtl' }}
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
              </Box>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Priority</Typography>
                <TextField {...register('priority', { valueAsNumber: true })} type="number" fullWidth size="small"
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
              </Box>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>From Date</Typography>
                <TextField {...register('from_date')} type="datetime-local" fullWidth size="small"
                  InputLabelProps={{ shrink: true }} InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
              </Box>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>To Date</Typography>
                <TextField {...register('to_date')} type="datetime-local" fullWidth size="small"
                  InputLabelProps={{ shrink: true }} InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
              </Box>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Min Mobile Version</Typography>
                <TextField {...register('from_mobile_version')} fullWidth size="small"
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Active</Typography>
                <Controller name="active" control={control} defaultValue={true}
                  render={({ field }) => <Switch checked={!!field.value} onChange={field.onChange} />} />
              </Box>
            </Box>
          )}

          {activeTab === 'categories' && (
            <WellnessCategoriesTab
              categories={categories}
              wellnessItems={wellnessItems}
              onAdd={() => { setEditingCategory(null); setCategoryDialogOpen(true); }}
              onEdit={(cat) => { setEditingCategory(cat); setCategoryDialogOpen(true); }}
              onDelete={(cat) => { setDeletingCategory(cat); setDeleteCategoryDialogOpen(true); }}
              onReorder={setCategories}
            />
          )}
        </Box>
      </ClinicDialog>

      <WellnessCategoryDialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        editingCategory={editingCategory}
        wellnessItems={wellnessItems}
        onSave={handleSaveCategory}
      />

      <ConfirmDeleteDialog
        open={deleteCategoryDialogOpen}
        onClose={() => { setDeleteCategoryDialogOpen(false); setDeletingCategory(null); }}
        onConfirm={handleConfirmDeleteCategory}
        entityType="Category"
        entityName={deletingCategory?.title}
      />

      <ConfirmDeleteDialog open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setConfigToDelete(null); }}
        onConfirm={() => crud.handleDelete(configToDelete)}
        entityType="Wellness Config" entityName={configToDelete?.name} />

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!currentUser?.user_privilege?.config} />
    </TablePageLayout>
  );
};

export default WellnessConfigPage;
